import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogOut, Mail } from "lucide-react";

export const ArtistProfile = ({ user, onSaved }) => {
  const [artistName, setArtistName] = useState(user.artist_name || "");
  const [bio, setBio] = useState(user.bio || "");
  const [paypalEmail, setPaypalEmail] = useState(user.paypal_email || "");
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const { logout, setUser } = useAuth();
  const navigate = useNavigate();

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailBusy(true);
    try {
      const { data } = await api.patch("/auth/email", { new_email: newEmail, password: emailPassword });
      toast.success(`Adresse du compte remplacée par ${data.email}`);
      setNewEmail("");
      setEmailPassword("");
      setUser?.((await api.get("/auth/me")).data);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setEmailBusy(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm("Confirmer la désinscription ? Vos morceaux et votre page publique seront retirés du catalogue (vos données sont conservées et le studio peut vous réactiver).")) {
      return;
    }
    try {
      await api.post("/studio/deactivate");
      toast.success("Votre compte artiste a été désinscrit. Vos morceaux ne sont plus visibles.");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/studio/profile", { artist_name: artistName, bio, paypal_email: paypalEmail });
      toast.success("Profil artiste mis à jour");
      onSaved?.(data);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="artist-profile">
      <h2 className="font-display text-2xl font-medium">Profil artiste</h2>
      {user.slug && (
        <p className="mt-2 text-xs text-zinc-500">
          Votre page publique :{" "}
          <Link to={`/artiste/${user.slug}`} data-testid="artist-public-link" className="text-amber-400 underline-offset-4 hover:underline">
            /artiste/{user.slug}
          </Link>
        </p>
      )}
      <form onSubmit={save} className="mt-6 space-y-5">
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Nom de scène</label>
          <input
            value={artistName}
            onChange={(e) => setArtistName(e.target.value)}
            data-testid="profile-artist-name"
            className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
            placeholder="Nom affiché sur le site"
          />
        </div>
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Bio</label>
          <textarea
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            data-testid="profile-bio"
            className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
            placeholder="Quelques mots sur votre univers musical…"
          />
        </div>
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Votre PayPal (pour recevoir vos reversements)</label>
          <input
            type="text"
            value={paypalEmail}
            onChange={(e) => setPaypalEmail(e.target.value)}
            data-testid="profile-paypal-email"
            className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
            placeholder="votre@email-paypal.fr ou paypal.me/votrenom"
          />
          <p className="mt-2 text-[11px] text-zinc-500">L'email de votre compte PayPal OU votre lien PayPal.me. Les reversements sont calculés tous les 30 jours et versés sur ce compte.</p>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="profile-save"
          className="border border-amber-500/60 px-6 py-3 text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
        >
          {busy ? "Enregistrement…" : "Enregistrer le profil"}
        </button>
      </form>

      <div className="mt-8 border-t border-white/[0.08] pt-6" data-testid="email-change-zone">
        <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          <Mail className="h-3.5 w-3.5" /> Adresse email du compte
        </p>
        <p className="mt-2 text-xs leading-relaxed text-zinc-500">
          Adresse actuelle : <span className="text-zinc-300" data-testid="current-email">{user.email}</span>.
          C'est elle qui sert à vous connecter et qui reçoit les notifications.
        </p>
        <form onSubmit={changeEmail} className="mt-4 space-y-3">
          <input
            type="email"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="email-change-new"
            className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
            placeholder="Nouvelle adresse email"
          />
          <input
            type="password"
            required
            value={emailPassword}
            onChange={(e) => setEmailPassword(e.target.value)}
            data-testid="email-change-password"
            className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500"
            placeholder="Confirmez avec votre mot de passe"
          />
          <button
            type="submit"
            disabled={emailBusy}
            data-testid="email-change-submit"
            className="border border-amber-500/60 px-6 py-3 text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
          >
            {emailBusy ? "Modification…" : "Changer mon adresse email"}
          </button>
        </form>
      </div>

      {user.role === "artist" && (
        <div className="mt-8 border-t border-white/[0.08] pt-6" data-testid="deactivate-zone">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Quitter la plateforme</p>
        <p className="mt-2 text-xs leading-relaxed text-zinc-500">
          L'inscription est gratuite et sans engagement : vous pouvez vous désinscrire à tout moment.
          Vos morceaux et votre page publique seront retirés du catalogue ; vos données et votre
          historique de ventes sont conservés, et le studio peut vous réactiver sur demande.
        </p>
        <button
          onClick={deactivate}
          data-testid="deactivate-account"
          className="mt-4 flex items-center gap-2 border border-red-500/40 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-red-400 transition-colors hover:bg-red-500 hover:text-black"
        >
          <LogOut className="h-3.5 w-3.5" /> Me désinscrire
        </button>
        </div>
      )}
    </section>
  );
};
